import { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { getErrorMessage } from '../api/client';
import { createShiftClose, getShiftCashBreakdown, getShiftClosePreview, getShiftCloses } from '../api/shifts';
import { ShiftCashBreakdownPanel } from '../components/ShiftCashBreakdownPanel';
import type { ShiftCashBreakdown } from '../types/shiftCashBreakdown';
import { getPendingSummary } from '../api/dashboard';
import { isTodayCovered } from '../utils/shiftToday';
import { Icon } from '../components/ui/Icon';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionTabs, type SectionTab } from '../components/ui/SectionTabs';
import { SummaryStrip } from '../components/ui/SummaryStrip';
import { CheckRow } from '../components/ui/CheckRow';
import { MetaList } from '../components/ui/MetaList';
import { StatSummary } from '../components/ui/StatSummary';
import { Button, EmptyPanel, LoadingPanel } from '../components/ui/primitives';
import type { ShiftCloseListItem } from '../types/shifts';
import { ShiftCloseChecklist } from '../components/ShiftCloseChecklist';
import { Answers, ChoiceCards, DoneRow, FooterReason, Question } from '../components/capture';
import { ProgressiveSection } from '../components/ui/ProgressiveSection';
import { getStores } from '../api/stores';
import { getUsers } from '../api/users';
import { useAuth } from '../auth/AuthProvider';
import { canViewShiftCloseHistory } from '../auth/permissions';
import { Modal } from '../components/Modal';
import { ShiftCloseHistoryPanel } from '../components/ShiftCloseHistoryPanel';
import { ShiftDailySummaryPanel } from '../components/ShiftDailySummaryPanel';
import type { CreateShiftCloseResult, ShiftClosePreview } from '../types/shifts';
import type { Store } from '../types/stores';
import type { User } from '../types/users';
import { WallDateTimeField } from '../components/WallDateTimeField';
import { formatMoney, formatOptionalMoney } from '../utils/costFormatting';
import { calculateExpectedCash, nativeCashLabel } from '../utils/shiftCloseCash';
import { cashDifferenceLabel, cashDifferenceTone, cashProgress, countedCashEcho, shiftSummaryLine } from '../utils/shiftCloseGuided';
import { useLoyversePresence } from '../utils/loyversePresence';
import { calculateDisabledReason, depositComplete, latestCloseEndAt, proposeShiftRange, saveDisabledReason, type DepositChoice } from '../utils/shiftProposal';
import { DEFAULT_STORE_TIMEZONE, dateToStoreISO, resolveStoreTimezone, storeWallTimeToIso } from '../utils/timezones';
import { userDisplayName, userDisplayNameOr } from '../utils/userDisplayName';

const CONFIRM_WORD = 'CERRAR';
// FI-22 · Entrega 2: hasta seis opciones excluyentes se muestran como tarjetas con círculo; más, desplegable.
const MAX_CHOICE_CARDS = 6;
const ALL_CASH_BLOCKS_OPEN = { cashOpen: true, deposit: true, counted: true };
// P-075: las vistas de la pantalla (antes, todo en una sola columna: hacer, consultar y analizar).
type ShiftView = 'close' | 'history' | 'daily';

/** Enter cierra el teclado en los campos de dinero (el bloque se encoge al perder el foco). */
function blurOnEnter(event: KeyboardEvent<HTMLInputElement>) {
  if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); }
}

export function ShiftClosePage() {
  const { user } = useAuth();
  const canViewHistory = canViewShiftCloseHistory(user?.role);
  // ZTV-04: con false se ocultan los KPIs del canal Loyverse; true/null conservan todo (un negocio
  // con Loyverse nunca pierde sus métricas por un parpadeo de la consulta de presencia).
  const loyverseConnected = useLoyversePresence();
  const showLoyverseData = loyverseConnected !== false;
  const [stores, setStores] = useState<Store[]>([]);
  const [employees, setEmployees] = useState<User[]>();
  const [filters, setFilters] = useState({ storeId: '', startAt: '', endAt: '' });
  const [employeeId, setEmployeeId] = useState('');
  const [preview, setPreview] = useState<ShiftClosePreview>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [countedCash, setCountedCash] = useState('');
  const [cashOpen, setCashOpen] = useState('');
  // FI-22 · Entrega 2: «¿Sacaste efectivo para depositar?» no viene preseleccionado (antes: No).
  const [depositChoice, setDepositChoice] = useState<DepositChoice>('');
  const depositApplies = depositChoice === 'yes';
  const [depositAccount, setDepositAccount] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [notes, setNotes] = useState('');
  // FI-22 · Entrega 2: bloques que se encogen. El «Turno» se cierra al calcular; cada pregunta de
  // efectivo se vuelve una fila con «Cambiar» al contestarla. Solo es presentación: nada se guarda.
  const [shiftOpen, setShiftOpen] = useState(true);
  const [openBlocks, setOpenBlocks] = useState(ALL_CASH_BLOCKS_OPEN);
  const openBlock = (key: keyof typeof ALL_CASH_BLOCKS_OPEN) => setOpenBlocks((current) => ({ ...current, [key]: true }));
  const closeBlock = (key: keyof typeof ALL_CASH_BLOCKS_OPEN) => setOpenBlocks((current) => ({ ...current, [key]: false }));
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [createResult, setCreateResult] = useState<CreateShiftCloseResult>();
  const [historyRefreshToken, setHistoryRefreshToken] = useState(0);
  // Fase 23B: estado del checklist del turno (formularios REQUIRED/OPTIONAL). requiredComplete=true por
  // defecto (un negocio sin formularios no se bloquea). El backend revalida al cerrar (autoritativo).
  const [checklist, setChecklist] = useState<{ requiredComplete: boolean; submittedIds: string[]; hasForms: boolean }>({ requiredComplete: true, submittedIds: [], hasForms: false });
  // Ola 3: la ACCIÓN del turno manda. EMPLOYEE ve "Tu turno de hoy" + sus cortes recientes (el
  // backend ya autoriza listar su tienda desde 16B-3); OWNER/MANAGER ven el estado de hoy del
  // negocio. El formulario sigue igual; el bloque solo lleva a él.
  const isEmployee = user?.role === 'EMPLOYEE';
  const [recentCloses, setRecentCloses] = useState<ShiftCloseListItem[]>();
  const [todayPendingStores, setTodayPendingStores] = useState<number>();
  // Historial completo bajo demanda para EMPLOYEE (sus recientes bastan casi siempre); un deep-link
  // #shifts/<id> (p. ej. desde Solicitudes) lo abre directo como antes.
  const deepLinkedCloseId = window.location.hash.replace('#', '').split('/')[1] || undefined;
  const [showFullHistory, setShowFullHistory] = useState(false);
  /**
   * P-075 · Vistas: «Hacer corte» (la de siempre al entrar) · «Historial» · «Resumen del día» (dueño y
   * gerente). Las tres quedan MONTADAS y la inactiva se oculta con `hidden`: cambiar de vista no pierde un
   * corte calculado, el efectivo contado, las notas ni los filtros del historial. Un deep-link
   * `#shifts/<id>` abre el detalle de ese corte, así que entra directo al Historial.
   */
  const [view, setView] = useState<ShiftView>(() => (deepLinkedCloseId !== undefined ? 'history' : 'close'));
  const viewTabs: SectionTab[] = [
    { id: 'close', label: 'Hacer corte' },
    // El empleado también tiene Historial: sus cortes recientes y, bajo demanda, el historial completo.
    ...(canViewHistory || isEmployee ? [{ id: 'history', label: 'Historial' }] : []),
    ...(canViewHistory && !isEmployee ? [{ id: 'daily', label: 'Resumen del día' }] : []),
  ];
  const formRef = useRef<HTMLElement>(null);
  const goToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const employeeStoreId = filters.storeId || (stores.length === 1 ? stores[0]?.id : undefined);
  const loadTodayState = useCallback(() => {
    if (isEmployee) {
      if (!employeeStoreId) return;
      getShiftCloses({ storeId: employeeStoreId, limit: 5 })
        .then((result) => setRecentCloses(result.items))
        .catch(() => setRecentCloses([]));
    } else {
      getPendingSummary()
        .then((summary) => setTodayPendingStores(summary.shiftClosePendingToday))
        .catch(() => { /* el bloque simplemente no afirma nada */ });
    }
  }, [isEmployee, employeeStoreId]);
  useEffect(() => { loadTodayState(); }, [loadTodayState]);

  const employeeStore = stores.find((store) => store.id === employeeStoreId);
  const employeeTimeZone = employeeStore?.timezone || DEFAULT_STORE_TIMEZONE;
  const todayCovered = recentCloses ? isTodayCovered(recentCloses, new Date(), employeeTimeZone) : undefined;
  const todayClose = recentCloses?.find((close) => isTodayCovered([close], new Date(), employeeTimeZone));

  // Pre-client UX hardening: "Hacer corte" aterriza en un formulario LISTO — el rango del turno se
  // propone solo (desde el último corte de HOY de la tienda, o desde las 00:00 del día de la
  // tienda) y queda visible y editable. Nunca pisa un rango que la persona ya tecleó, y jamás
  // guarda nada por sí mismo: "Calcular corte" y "Guardar" siguen siendo decisiones explícitas.
  const proposalBusyRef = useRef(false);
  // Último rango PROPUESTO (no tecleado): si la persona cambia de tienda sin haberlo tocado, se
  // limpia para proponer el de la nueva tienda; un rango editado a mano jamás se pisa.
  const proposedRangeRef = useRef<{ startAt: string; endAt: string }>();
  useEffect(() => {
    if (filters.startAt !== '' || filters.endAt !== '') return; // hay rango del usuario: no tocarlo
    if (isEmployee) {
      if (!employeeStoreId || recentCloses === undefined) return; // aún sin datos: no proponer a ciegas
      const proposal = proposeShiftRange(latestCloseEndAt(recentCloses), new Date(), employeeTimeZone);
      proposedRangeRef.current = proposal;
      setFilters((current) => (current.startAt === '' && current.endAt === ''
        ? { ...current, storeId: current.storeId || employeeStoreId, startAt: proposal.startAt, endAt: proposal.endAt }
        : current));
      return;
    }
    if (!filters.storeId || proposalBusyRef.current) return;
    proposalBusyRef.current = true;
    const storeId = filters.storeId;
    const timeZone = resolveStoreTimezone(stores, { storeId });
    getShiftCloses({ storeId, limit: 5 })
      .then((result) => {
        const proposal = proposeShiftRange(latestCloseEndAt(result.items), new Date(), timeZone);
        proposedRangeRef.current = proposal;
        // Solo si el rango sigue vacío y la tienda no cambió mientras respondía el backend.
        setFilters((current) => (current.storeId === storeId && current.startAt === '' && current.endAt === ''
          ? { ...current, startAt: proposal.startAt, endAt: proposal.endAt }
          : current));
      })
      .catch(() => { /* sin propuesta no se bloquea nada: el rango se captura a mano */ })
      .finally(() => { proposalBusyRef.current = false; });
  }, [isEmployee, employeeStoreId, employeeTimeZone, recentCloses, filters.storeId, filters.startAt, filters.endAt, stores]);

  useEffect(() => {
    void getStores().then((result) => {
      setStores(result.stores);
      // Fase 16B-2: si solo tiene una tienda asignada (el caso típico de un EMPLOYEE), se
      // preselecciona para no obligarlo a elegir entre una sola opción.
      if (result.stores.length === 1) {
        setFilters((current) => (current.storeId ? current : { ...current, storeId: result.stores[0]!.id }));
      }
    }).catch((loadError) => setError(getErrorMessage(loadError, 'No fue posible cargar las tiendas.')));
    // Fase 19A: empleado responsable del turno — replica listEmployeesForStores_ de Apps Script. Un
    // EMPLOYEE solo puede registrar su propio corte (validado también en el backend); OWNER/MANAGER
    // pueden elegir cualquier empleado.
    if (user?.role === 'EMPLOYEE') {
      setEmployeeId(user.id);
    } else {
      void getUsers().then((result) => setEmployees(result.users.filter((employee) => employee.isActive))).catch(() => setEmployees([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fase 19A: la lista de empleados elegibles se acota a los asignados a la tienda elegida (más el
  // propio OWNER, que siempre puede operar cualquier tienda) — mismo criterio que Apps Script.
  const eligibleEmployees = (employees ?? []).filter((employee) => employee.role === 'OWNER' || employee.stores.some((store) => store.id === filters.storeId));

  // Cambiar de tienda limpia el rango solo si seguía siendo el PROPUESTO (nunca uno tecleado).
  const chooseStore = (nextStoreId: string) => setFilters((current) => {
    const untouched = proposedRangeRef.current && current.startAt === proposedRangeRef.current.startAt && current.endAt === proposedRangeRef.current.endAt;
    return { ...current, storeId: nextStoreId, ...(untouched ? { startAt: '', endAt: '' } : {}) };
  });

  const calculate = async (event: FormEvent) => {
    event.preventDefault();
    if (isLoading || calcReason !== null) return;
    setIsLoading(true);
    setError(undefined);
    setCreateResult(undefined);
    try {
      // Las horas tecleadas son hora de la TIENDA elegida, no del dispositivo: el mismo turno debe
      // dar el mismo corte sin importar desde qué ciudad se calcule.
      const timeZone = resolveStoreTimezone(stores, { storeId: filters.storeId });
      const result = await getShiftClosePreview({
        storeId: filters.storeId,
        startAt: storeWallTimeToIso(filters.startAt, timeZone),
        endAt: storeWallTimeToIso(filters.endAt, timeZone),
      });
      setPreview(result);
      setShiftOpen(false);
    } catch (requestError) {
      setPreview(undefined);
      setError(getErrorMessage(requestError, 'No fue posible calcular el corte de turno.'));
    } finally {
      setIsLoading(false);
    }
  };

  const storeName = stores.find((store) => store.id === filters.storeId)?.name ?? null;
  const employeeName = user?.role === 'EMPLOYEE'
    ? userDisplayNameOr(user)
    : (userDisplayName(eligibleEmployees.find((employee) => employee.id === employeeId)) ?? '');
  const countedCashNumber = Number(countedCash);
  const cashOpenNumber = Number(cashOpen || 0);
  const depositAmountNumber = Number(depositAmount || 0);
  const expectedCashPreview = preview
    ? calculateExpectedCash({
        cashOpen: cashOpenNumber,
        loyverseCashTotal: preview.loyverseCashTotal === null ? null : Number(preview.loyverseCashTotal),
        otherSalesCashTotal: Number(preview.otherSalesCashTotal),
        purchasesTotal: Number(preview.purchasesTotal),
        otherExpensesTotal: Number(preview.otherExpensesTotal),
        depositApplies,
        depositAmount: depositAmountNumber,
      })
    : null;
  const cashDifferencePreview = expectedCashPreview !== null && countedCash !== '' ? countedCashNumber - expectedCashPreview : null;
  const expectedCash = expectedCashPreview ?? 0;
  const differenceTone = cashDifferenceTone(cashDifferencePreview);
  const progress = cashProgress(expectedCash, countedCash);

  // Detalle del turno (solo lectura): el backend arma el desglose con los MISMOS criterios que el
  // cálculo del corte. Se recarga cuando cambian los valores que el usuario captura (caja inicial,
  // contado, depósito) para que el esperado provisional del detalle coincida con el de arriba.
  const [breakdown, setBreakdown] = useState<ShiftCashBreakdown>();
  const [breakdownError, setBreakdownError] = useState<string>();
  useEffect(() => {
    if (!preview) { setBreakdown(undefined); return; }
    let alive = true;
    getShiftCashBreakdown({
      storeId: preview.storeId,
      startAt: preview.startAt,
      endAt: preview.endAt,
      cashOpen: cashOpenNumber || 0,
      ...(countedCash !== '' ? { countedCash: countedCashNumber } : {}),
      depositAmount: depositApplies ? depositAmountNumber || 0 : 0,
    })
      .then((result) => { if (alive) { setBreakdown(result); setBreakdownError(undefined); } })
      .catch(() => { if (alive) { setBreakdown(undefined); setBreakdownError('No fue posible cargar el detalle del turno; el corte se puede guardar igual.'); } });
    return () => { alive = false; };
  }, [preview, cashOpenNumber, countedCash, countedCashNumber, depositApplies, depositAmountNumber]);
  // Pre-client UX hardening: misma condición de siempre, pero con la razón VISIBLE (patrón
  // Ticket Mixto: botón deshabilitado + explicación concreta de qué falta).
  const saveReason = saveDisabledReason({ employeeId, cashOpen, countedCash, deposit: depositChoice, depositAccount, depositAmount, requiredFormsComplete: checklist.requiredComplete });
  const calcReason = calculateDisabledReason({ storeId: filters.storeId, employeeId, startAt: filters.startAt, endAt: filters.endAt });
  const formTimeZone = resolveStoreTimezone(stores, { storeId: filters.storeId || employeeStoreId || null });
  const depositDone = depositChoice === 'no' || (depositChoice === 'yes' && depositComplete({ depositAccount, depositAmount }));
  const closeDepositIfComplete = () => { if (depositComplete({ depositAccount, depositAmount })) closeBlock('deposit'); };
  // P-079 (Codex R1 · K10): un bloque solo se pone en verde si su valor es VÁLIDO (lo que el campo ya exigía: un número
  // mayor o igual que cero), no solo «distinto de vacío»; si no, se queda abierto con su mensaje.
  const nonNegative = (text: string) => text !== '' && Number.isFinite(Number(text)) && Number(text) >= 0;
  const cashOpenValid = nonNegative(cashOpen);
  const depositAmountValid = depositChoice !== 'yes' || nonNegative(depositAmount);
  const countedValid = nonNegative(countedCash);

  return <div className="n3-screen shift-close-page">
    {/* P-075: UN encabezado (antes, h2 «Corte de turno» con la ayuda repetida debajo). */}
    <PageHeader
      description={canViewHistory
        ? 'Cierra un periodo de operación de una tienda: ventas, tickets y efectivo del rango. No modifica ventas ni inventario.'
        : 'Cierra tu turno: ventas, tickets y efectivo del rango. No modifica ventas ni inventario. Solo puedes consultar tus propios cortes; los de toda la tienda los administra tu Dueño o Gerente.'}
      title="Cortes de turno"
    />
    {error && <div className="alert alert--error">{error}</div>}
    {viewTabs.length > 1 && <SectionTabs active={view} label="Vistas de Cortes de turno" onSelect={(id) => setView(id as ShiftView)} tabs={viewTabs} />}

    {/* Sin clase en el contenedor de cada vista: una clase con `display` le ganaría a `hidden`. */}
    <div hidden={view !== 'close'}>
    {/* Ola 3 — el bloque de HOY manda: primero hacer, después consultar. */}
    {isEmployee && employeeStoreId && todayCovered !== undefined && (
      <section aria-label="Tu turno de hoy" className={`shift-today ${todayCovered ? 'shift-today--done' : ''}`}>
        <div className="shift-today__body">
          <span className="shift-today__eyebrow">Tu turno de hoy{employeeStore ? ` · ${employeeStore.name}` : ''}</span>
          {todayCovered
            ? <strong>Corte de hoy registrado ✓{todayClose?.folio ? ` · ${todayClose.folio}` : ''}</strong>
            : <strong>Aún no registras el corte de hoy</strong>}
          <span className="shift-today__hint">{todayCovered ? 'Puedes revisarlo en «Historial», con tus cortes recientes.' : 'Cuando termine el turno, calcula y guarda tu corte.'}</span>
        </div>
        {todayCovered
          ? <button className="button button--secondary" onClick={goToForm} type="button">Hacer otro corte</button>
          : <button className="button button--primary" onClick={goToForm} type="button">Hacer corte</button>}
      </section>
    )}
    {/* P-075: el servidor cuenta SOLO tickets de Loyverse (`pendingSummaryService`). Sin Loyverse ese
        conteo siempre da 0 y el bloque afirmaba «al día» sin saberlo: en ese caso no se afirma nada.
        Con Loyverse (o presencia aún desconocida) queda como siempre. */}
    {!isEmployee && todayPendingStores !== undefined && loyverseConnected !== false && (
      <section aria-label="Cortes de hoy" className={`shift-today ${todayPendingStores === 0 ? 'shift-today--done' : ''}`}>
        <div className="shift-today__body">
          <span className="shift-today__eyebrow">Hoy</span>
          {todayPendingStores > 0
            ? <strong>{todayPendingStores} tienda(s) con ventas de hoy sin corte</strong>
            : <strong>Cortes de hoy al día ✓</strong>}
          <span className="shift-today__hint">{todayPendingStores > 0 ? 'Registra el corte cuando termine el turno de cada tienda.' : 'No hay tiendas con ventas de hoy sin su corte.'}</span>
        </div>
        {todayPendingStores > 0 && <button className="button button--primary" onClick={goToForm} type="button">Hacer corte</button>}
      </section>
    )}
    {!isEmployee && canViewHistory && loyverseConnected === false && (
      <p className="wiz-hint">Revisa en el <button className="link-button" onClick={() => setView('history')} type="button">Historial</button> si ya hay corte de hoy.</p>
    )}

    <section className="preview-section shift-close-new-section" ref={formRef}>
      {/* P-075: la ayuda del rango propuesto vive junto a las horas del turno (aquí se repetía). */}
      <div className="preview-section__heading"><div><h3>Nuevo corte</h3></div></div>
      <div className="shift-guided">
        {/* Bloque «Turno»: tienda, responsable e inicio/fin. Se encoge a una fila al calcular. */}
        {preview && !shiftOpen
          ? <DoneRow label="Turno" value={shiftSummaryLine({ storeName, employeeName, startAt: filters.startAt, endAt: filters.endAt })} onChange={() => setShiftOpen(true)} />
          : <form className="shift-guided__form" onSubmit={(event) => void calculate(event)}>
            {stores.length > 1 && <>
              <Question>¿De qué tienda es el corte?</Question>
              <ChoiceCards ariaLabel="Tienda del corte" options={stores.map((store) => ({ value: store.id, label: store.name }))} stack value={filters.storeId} onChange={chooseStore} />
            </>}
            {user?.role !== 'EMPLOYEE' && <>
              <Question>¿Quién fue el responsable del turno?</Question>
              {eligibleEmployees.length <= MAX_CHOICE_CARDS
                ? <ChoiceCards ariaLabel="Empleado responsable" options={eligibleEmployees.map((employee) => ({ value: employee.id, label: userDisplayNameOr(employee) }))} value={employeeId} onChange={setEmployeeId} />
                : <Answers><label className="field"><span>Empleado responsable</span><select onChange={(event) => setEmployeeId(event.target.value)} value={employeeId}><option value="">Selecciona un empleado</option>{eligibleEmployees.map((employee) => <option key={employee.id} value={employee.id}>{userDisplayNameOr(employee)}</option>)}</select></label></Answers>}
            </>}
            <Question>¿A qué hora empezó y terminó el turno?</Question>
            <p className="wiz-hint">Te proponemos desde tu último corte de hoy hasta ahora; ajústalo si fue distinto. Hora local de la tienda.</p>
            <Answers>
              <WallDateTimeField label="Inicio del turno" timeZone={formTimeZone} value={filters.startAt} onChange={(startAt) => setFilters({ ...filters, startAt })} />
              <WallDateTimeField label="Fin del turno" timeZone={formTimeZone} value={filters.endAt} onChange={(endAt) => setFilters({ ...filters, endAt })} />
            </Answers>
            <div className="shift-guided__actions">
              <button className="button button--primary" disabled={isLoading || calcReason !== null} type="submit">{isLoading ? 'Calculando...' : preview ? 'Volver a calcular' : 'Calcular corte'}</button>
              {!isLoading && <FooterReason>{calcReason}</FooterReason>}
            </div>
          </form>}

        {isLoading && <div className="inline-loading"><span className="spinner" />Calculando corte de turno...</div>}
        {createResult && <div className="alert alert--success">{createResult.message}</div>}

        {preview && !isLoading && <>
          {/* Tickets huérfanos: si hay ventas fuera del rango elegido que NINGÚN corte cubre, lo más
              probable es que el inicio/fin estén mal tecleados — el dinero sí está en el cajón y el
              corte saldría con sobrante artificial. Aviso, no bloqueo (puede ser legítimo). */}
          {preview.uncoveredTickets && preview.uncoveredTickets.before.count > 0 && <div className="alert alert--warning">
            ⚠ Hay <strong>{preview.uncoveredTickets.before.count} venta(s) por ${preview.uncoveredTickets.before.amount}</strong> ANTES de tu hora de inicio (últimas {preview.uncoveredTickets.windowHours} h) que no pertenecen a ningún corte. ¿Seguro que el turno empezó a esa hora? Si no, corrige el <strong>inicio del turno</strong> antes de guardar — si guardas así, esas ventas quedarán fuera y el corte marcará sobrante.
          </div>}
          {preview.uncoveredTickets && preview.uncoveredTickets.after.count > 0 && <div className="alert alert--warning">
            ⚠ Hay <strong>{preview.uncoveredTickets.after.count} venta(s) por ${preview.uncoveredTickets.after.amount}</strong> DESPUÉS de tu hora de fin que no pertenecen a ningún corte. Si son de este turno, corrige el <strong>fin del turno</strong>; si son del siguiente, asegúrate de que ese corte los cubra.
          </div>}
          {/* ZTV-04: sin integración Loyverse conectada, los KPIs de ese canal no dominan el corte
              (serían cuatro ceros); con conexión (o presencia aún desconocida) todo queda igual. */}
          {/* P-075: las mismas siete cifras con las mismas condiciones, pero una sola a la vista (el efectivo
              esperado, el mismo número que la barra de abajo) y el resto en el desglose: en el teléfono las
              siete tarjetas apiladas empujaban las preguntas del efectivo fuera de la pantalla. */}
          <SummaryStrip
            details={[
              ...(showLoyverseData ? [
                { label: 'Tickets Loyverse', value: preview.totalReceipts },
                { label: 'Venta bruta Loyverse', value: formatOptionalMoney(preview.grossSales) },
                { label: 'Venta neta Loyverse', value: formatOptionalMoney(preview.netSales) },
                { label: 'Ventas Loyverse en efectivo', value: formatOptionalMoney(preview.loyverseCashTotal) },
              ] : []),
              { label: nativeCashLabel(showLoyverseData), value: formatOptionalMoney(preview.otherSalesCashTotal) },
              { label: 'Gastos', value: formatOptionalMoney(preview.otherExpensesTotal) },
              { label: 'Compras', value: formatOptionalMoney(preview.purchasesTotal) },
            ]}
            detailsLabel="Ver ventas, gastos y compras"
            label="Resumen del corte"
            primary={{ label: 'Efectivo esperado', value: formatMoney(expectedCash.toFixed(2)) }}
          />

          {showLoyverseData && preview.loyverseCashTotal === null && <div className="alert alert--warning">No se encontró desglose confiable de efectivo en las ventas Loyverse sincronizadas. Se trata como $0.00 en el efectivo esperado; puedes guardar el corte igual.</div>}
          {preview.warnings.filter((warning) => !warning.includes('desglose confiable de métodos de pago')).map((warning, index) => <div className="alert alert--warning" key={index}>{warning}</div>)}

          {/* Efectivo: barra esperado vs. contado + tres preguntas que se encogen. El depósito va
              ANTES de contar para que el eco «cuadra / faltan / sobran» ya lo tenga descontado. */}
          <div className="wiz-progress" aria-live="polite">
            <div className="wiz-progress__text"><span>Efectivo en caja</span><span>{progress.label}</span></div>
            <div className="wiz-progress__bar"><div className={`wiz-progress__fill${progress.tone === 'ok' ? ' wiz-progress__fill--ok' : progress.tone === 'none' ? '' : ' wiz-progress__fill--warn'}`} style={{ width: `${progress.percent}%` }} /></div>
          </div>

          {/* P-079: los tres bloques del efectivo como secciones progresivas (la pieza común): se encogen al salir del
              campo con un valor VÁLIDO (el mismo `closeBlock` de siempre); un valor inválido se queda abierto con su
              mensaje; «Cambiar» reabre con el valor puesto. El bloque «Turno» conserva su regla propia (se encoge al
              calcular): no es validación local y no se generaliza (Codex R1 · K17). */}
          <ProgressiveSection busy={isLoading} complete={cashOpenValid} error={cashOpen !== '' && !cashOpenValid ? 'La caja inicial debe ser un número mayor o igual que cero.' : undefined} label="Caja inicial" onOpen={() => openBlock('cashOpen')} open={openBlocks.cashOpen} summary={cashOpenValid ? formatMoney(cashOpen) : ''}>
            <Question>¿Con cuánto abrió la caja?</Question>
            <p className="wiz-hint">El efectivo que había al iniciar el turno (puede ser 0).</p>
            <Answers><label className="field wiz-big-input"><span>Caja inicial (MXN)</span><input enterKeyHint="done" inputMode="decimal" min="0" onBlur={() => { if (cashOpen !== '') closeBlock('cashOpen'); }} onChange={(event) => setCashOpen(event.target.value)} onKeyDown={blurOnEnter} placeholder="0.00" step="any" type="number" value={cashOpen} /></label></Answers>
          </ProgressiveSection>

          <ProgressiveSection busy={isLoading} complete={depositDone && depositAmountValid} error={depositChoice === 'yes' && depositAmount !== '' && !depositAmountValid ? 'El monto depositado debe ser un número mayor o igual que cero.' : undefined} errorControl="shift-deposit-amount" label="Depósito" onOpen={() => openBlock('deposit')} open={openBlocks.deposit} summary={depositChoice === 'no' ? 'No hubo' : depositDone ? `${formatMoney(depositAmount)} · ${depositAccount.trim()}` : ''}>
            <Question>¿Sacaste efectivo para depositar?</Question>
            <ChoiceCards
              ariaLabel="Depósito del turno"
              options={[{ value: 'no', label: 'No', hint: 'Todo el efectivo sigue en el cajón' }, { value: 'yes', label: 'Sí', hint: 'Se descuenta del efectivo esperado' }]}
              value={depositChoice}
              onChange={(choice) => { setDepositChoice(choice); setOpenBlocks((current) => ({ ...current, deposit: choice === 'yes' })); }}
            />
            {depositApplies && <Answers>
              <label className="field"><span>Cuenta o tarjeta del depósito</span><input enterKeyHint="next" onBlur={closeDepositIfComplete} onChange={(event) => setDepositAccount(event.target.value)} onKeyDown={blurOnEnter} placeholder="Ej. Cuenta BBVA" value={depositAccount} /></label>
              <label className="field wiz-big-input"><span>Monto depositado (MXN)</span><input enterKeyHint="done" id="shift-deposit-amount" inputMode="decimal" min="0" onBlur={closeDepositIfComplete} onChange={(event) => setDepositAmount(event.target.value)} onKeyDown={blurOnEnter} placeholder="0.00" step="any" type="number" value={depositAmount} /></label>
            </Answers>}
          </ProgressiveSection>

          <ProgressiveSection busy={isLoading} complete={countedValid} error={countedCash !== '' && !countedValid ? 'El efectivo contado debe ser un número mayor o igual que cero.' : undefined} label="Efectivo contado" onOpen={() => openBlock('counted')} open={openBlocks.counted} summary={countedValid ? <>{formatMoney(countedCash)}<span className={`wiz-tag ${differenceTone === 'ok' ? 'wiz-tag--ok' : 'wiz-tag--warn'}`}>{cashDifferenceLabel(cashDifferencePreview)}</span></> : ''}>
              <Question>¿Cuánto efectivo contaste?</Question>
              <p className="wiz-hint">Todo el efectivo del cajón al cerrar, incluida la caja inicial.</p>
              <Answers>
                <label className="field wiz-big-input">
                  <span>Efectivo contado (MXN)</span>
                  <div className="wiz-inline">
                    <input enterKeyHint="done" inputMode="decimal" min="0" onBlur={() => { if (countedCash !== '') closeBlock('counted'); }} onChange={(event) => setCountedCash(event.target.value)} onKeyDown={blurOnEnter} placeholder="0.00" step="any" type="number" value={countedCash} />
                    <div aria-live="polite" className={`wiz-echo wiz-echo--big${cashDifferencePreview === null ? ' is-empty' : differenceTone === 'ok' ? '' : ' is-warn'}`}>{countedCashEcho(expectedCash, cashDifferencePreview)}</div>
                  </div>
                </label>
              </Answers>
          </ProgressiveSection>

          <label className="field shift-guided__notes"><span>Notas <small>(opcional)</small></span><input onChange={(event) => setNotes(event.target.value)} placeholder="Algo que deba saber quien revise el corte" value={notes} /></label>

          {/* Detalle del turno ANTES de confirmar: permite cazar la compra/gasto que falta o el medio
              de pago equivocado mientras todavía se puede corregir. Solo lectura. Sin
              financials.viewCosts (EMPLOYEE) el backend manda solo totales y conteos por grupo
              (revisión B-1, 2026-09-16): los grupos llegan sin renglones y no se despliegan. */}
          {breakdown && <ShiftCashBreakdownPanel breakdown={breakdown} onOpenReference={(line) => {
            if (line.kind === 'PURCHASE') window.location.hash = `purchases/${line.referenceId}`;
            else if (line.kind === 'EXPENSE') window.location.hash = `expenses/${line.referenceId}`;
            else if (line.kind === 'OTHER_SALE') window.location.hash = `other-sales/${line.referenceId}`;
          }} />}
          {breakdownError && <p className="shift-close-difference-note">{breakdownError}</p>}

          <ShiftCloseChecklist storeId={preview.storeId} startAt={preview.startAt} onStatusChange={setChecklist} />

          <div className="shift-guided__actions">
            <button className="button button--primary" disabled={saveReason !== null} onClick={() => setIsConfirmModalOpen(true)} type="button">Guardar corte</button>
            <FooterReason>{saveReason}</FooterReason>
          </div>
        </>}
      </div>
    </section>
    </div>

    {/* «Historial». La clase de rejilla va solo en la vista activa (con ella, `display: grid` le ganaría a
        `hidden`): separa la consulta del empleado del historial completo. */}
    {(canViewHistory || isEmployee) && <div className={view === 'history' ? 'n3-screen' : undefined} hidden={view !== 'history'}>
    {/* Ola 3 — CONSULTA del empleado: sus cortes recientes (solo identidad y estado, sin cifras).
        El historial completo sigue disponible, pero bajo demanda — no compite con la operación. */}
    {isEmployee && recentCloses && recentCloses.length > 0 && (
      <section aria-label="Mis cortes recientes" className="content-card shift-recent">
        <div className="content-card__header"><div><span className="eyebrow">Consulta</span><h2>Mis cortes recientes</h2></div>{canViewHistory && !showFullHistory && <button className="link-button" onClick={() => setShowFullHistory(true)} type="button">Ver historial completo</button>}</div>
        <ul className="shift-recent__rows">
          {recentCloses.map((close) => (
            <li className="shift-recent__row" key={close.id}>
              <span className="shift-recent__folio">{close.folio ?? 'Corte'}</span>
              <span className="shift-recent__range">{formatDateTime(close.startAt, employeeTimeZone)} – {formatDateTime(close.endAt, employeeTimeZone)}</span>
              {close.status === 'VOIDED'
                ? <span className="status-pill status-pill--neutral">Anulado</span>
                : close.hasPendingVoidRequest
                  ? <span className="status-pill">Con solicitud</span>
                  : <span className="status-pill status-pill--neutral">Registrado</span>}
            </li>
          ))}
        </ul>
      </section>
    )}
    {/* P-075: antes, sin cortes recientes no había nada que tocar (ni la vía al historial completo). */}
    {isEmployee && recentCloses === undefined && employeeStoreId && <LoadingPanel label="Cargando tus cortes recientes…" />}
    {isEmployee && !(recentCloses && recentCloses.length > 0) && !(recentCloses === undefined && employeeStoreId) && !showFullHistory && deepLinkedCloseId === undefined && (
      <EmptyPanel
        action={canViewHistory ? <Button onClick={() => setShowFullHistory(true)} variant="secondary">Ver historial completo</Button> : undefined}
        description="Aquí verás tus últimos cortes en cuanto guardes uno."
        icon="shift"
        title="Aún no tienes cortes recientes"
      />
    )}

    {canViewHistory && (!isEmployee || showFullHistory || deepLinkedCloseId !== undefined) && <ShiftCloseHistoryPanel refreshToken={historyRefreshToken} stores={stores} initialOpenId={deepLinkedCloseId} />}
    </div>}

    {/* Ola 3 — el resumen diario es ANÁLISIS y solo administrativo; el análisis completo vive en Reportes.
        P-075: antes era un desplegable al pie de la página; ahora es su vista (montada desde el inicio, como
        lo estaba dentro del desplegable cerrado). */}
    {canViewHistory && !isEmployee && <div hidden={view !== 'daily'}>
      <ShiftDailySummaryPanel
        actions={<button className="link-button" onClick={() => { window.location.hash = 'owner-dashboard'; }} type="button">Ver Reportes <Icon name="chevron-right" size={14} /></button>}
        refreshToken={historyRefreshToken}
        stores={stores}
      />
    </div>}

    {isConfirmModalOpen && preview && <ConfirmShiftCloseModal
      countedCash={countedCashNumber}
      cashOpen={cashOpenNumber}
      employeeId={employeeId}
      employeeName={employeeName}
      depositApplies={depositApplies}
      depositAccount={depositAccount}
      depositAmount={depositAmountNumber}
      expectedCashPreview={expectedCashPreview}
      notes={notes}
      formSubmissionIds={checklist.submittedIds}
      preview={preview}
      storeName={storeName}
      stores={stores}
      onClose={() => setIsConfirmModalOpen(false)}
      onSaved={(result) => {
        setCreateResult(result);
        setIsConfirmModalOpen(false);
        setHistoryRefreshToken((token) => token + 1);
        loadTodayState();
        // El corte ya quedó guardado en el historial (panel de abajo) — limpiar "Nuevo corte" para
        // que no se quede con los datos del corte anterior listos para volver a calcular/guardar.
        setPreview(undefined);
        setFilters((current) => ({ ...current, startAt: '', endAt: '' }));
        setCountedCash('');
        setCashOpen('');
        setDepositChoice('');
        setDepositAccount('');
        setDepositAmount('');
        setNotes('');
        setShiftOpen(true);
        setOpenBlocks(ALL_CASH_BLOCKS_OPEN);
        if (user?.role !== 'EMPLOYEE') setEmployeeId('');
      }}
    />}
  </div>;
}

function ConfirmShiftCloseModal({ preview, storeName, countedCash, cashOpen, employeeId, employeeName, depositApplies, depositAccount, depositAmount, expectedCashPreview, notes, formSubmissionIds, stores, onClose, onSaved }: {
  preview: ShiftClosePreview;
  storeName: string | null;
  countedCash: number;
  cashOpen: number;
  employeeId: string;
  employeeName: string;
  depositApplies: boolean;
  depositAccount: string;
  depositAmount: number;
  expectedCashPreview: number | null;
  notes: string;
  formSubmissionIds: string[];
  stores: Store[];
  onClose: () => void;
  onSaved: (result: CreateShiftCloseResult) => void;
}) {
  const [confirmText, setConfirmText] = useState('');
  const [dateAck, setDateAck] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  // ZTV-04: mismo criterio que la página — sin Loyverse conectado no se listan sus renglones en cero.
  const showLoyverseData = useLoyversePresence() !== false;
  const timeZone = resolveStoreTimezone(stores, { storeId: preview.storeId });
  // Corte de fecha pasada: exige confirmar día+horas (evita cerrar con la fecha de hoy por error).
  // "Pasada" se decide con el día de la TIENDA, no el del dispositivo.
  const isPastShift = dateToStoreISO(new Date(preview.startAt), timeZone) < dateToStoreISO(new Date(), timeZone);

  const cashDifference = expectedCashPreview !== null ? (countedCash - expectedCashPreview).toFixed(2) : null;

  const confirmSave = async () => {
    if (confirmText !== CONFIRM_WORD || isSaving) return;
    setIsSaving(true);
    setError(undefined);
    try {
      const result = await createShiftClose({
        storeId: preview.storeId,
        startAt: preview.startAt,
        endAt: preview.endAt,
        employeeId,
        cashOpen,
        depositApplies,
        depositAccount: depositApplies ? depositAccount.trim() : null,
        depositAmount: depositApplies ? depositAmount : null,
        countedCash,
        notes: notes.trim() || null,
        formSubmissionIds: formSubmissionIds.length > 0 ? formSubmissionIds : undefined,
        confirm: true,
      });
      onSaved(result);
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'No fue posible guardar el corte de turno.'));
    } finally {
      setIsSaving(false);
    }
  };

  return <Modal
    title="Confirmar corte de turno"
    isBusy={isSaving}
    onClose={onClose}
    footer={<>
      <button className="button button--ghost" disabled={isSaving} onClick={onClose} type="button">Cancelar</button>
      <button className="button button--primary" disabled={isSaving || confirmText !== CONFIRM_WORD || (isPastShift && !dateAck)} onClick={() => void confirmSave()} type="button">{isSaving ? 'Guardando...' : 'Guardar corte'}</button>
    </>}
  >
    {error && <div className="alert alert--error">{error}</div>}
    {/* P-076: primero lo que se confirma (esperado, contado, diferencia); el resto de los datos, todos a la
        vista, como lista etiqueta/valor. Son los mismos datos de antes. */}
    <StatSummary
      label="Lo que se va a guardar"
      stats={[
        { label: 'Efectivo esperado', value: expectedCashPreview === null ? 'No disponible' : formatOptionalMoney(expectedCashPreview.toFixed(2)) },
        { label: 'Efectivo contado', value: formatOptionalMoney(countedCash.toFixed(2)) },
        { label: 'Diferencia', value: cashDifference === null ? 'No disponible' : formatOptionalMoney(cashDifference), tone: cashDifference === null || Number(cashDifference) === 0 ? 'default' : Number(cashDifference) < 0 ? 'danger' : 'warning' },
      ]}
    />
    <MetaList
      aria-label="Datos del corte"
      columns
      items={[
        { label: 'Tienda', value: storeName ?? preview.storeName },
        { label: 'Empleado responsable', value: employeeName || 'Sin nombre' },
        { label: 'Rango', value: `${formatDateTime(preview.startAt, timeZone)} – ${formatDateTime(preview.endAt, timeZone)}` },
        ...(showLoyverseData ? [{ label: 'Tickets Loyverse', value: preview.totalReceipts }, { label: 'Venta bruta Loyverse', value: formatOptionalMoney(preview.grossSales) }] : []),
        { label: nativeCashLabel(showLoyverseData), value: formatOptionalMoney(preview.otherSalesCashTotal) },
        { label: 'Gastos', value: formatOptionalMoney(preview.otherExpensesTotal) },
        { label: 'Compras', value: formatOptionalMoney(preview.purchasesTotal) },
        { label: 'Caja inicial', value: formatOptionalMoney(cashOpen.toFixed(2)) },
        ...(depositApplies ? [{ label: 'Depósito', value: `${formatOptionalMoney(depositAmount.toFixed(2))} (${depositAccount})` }] : []),
      ]}
    />
    <div className="alert alert--warning">Al guardar, se creará un registro histórico del corte. Esto no modifica ventas ni inventario.</div>
    {isPastShift && <>
      <div className="alert alert--warning">Estás cerrando un corte de una <strong>fecha pasada</strong>. Verifica que el día y las horas del turno sean correctos.</div>
      <CheckRow checked={dateAck} label="La fecha y las horas del corte son correctas" onChange={setDateAck} />
    </>}
    <label className="field">
      <span>Escribe {CONFIRM_WORD} para confirmar</span>
      <input onChange={(event) => setConfirmText(event.target.value)} placeholder={CONFIRM_WORD} value={confirmText} />
    </label>
    {/* Patrón Ticket Mixto (ZTV-07): un botón deshabilitado siempre dice qué falta — nunca "no pasa nada". */}
    {!isSaving && (confirmText !== CONFIRM_WORD || (isPastShift && !dateAck)) && (
      <p className="section-hint">
        {confirmText !== CONFIRM_WORD
          ? `Escribe ${CONFIRM_WORD} en el campo de arriba para habilitar "Guardar corte".`
          : 'Marca la casilla de la fecha pasada para habilitar "Guardar corte".'}
      </p>
    )}
  </Modal>;
}

function formatDateTime(value: string, timeZone: string = DEFAULT_STORE_TIMEZONE) { return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(new Date(value)); }
